import React, { useState, useEffect, useRef, useMemo } from "react";
import { ScrollView, View, StyleSheet, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { Menu } from "@/components/Menu";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";
import { useLocalSearchParams, router } from "expo-router";
import useContextMenu from "@/hooks/useContextMenu";
import Badge from "@/components/ui/Badge";
import { getEntityPermissions } from "@/lib/permissions";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { PositionType } from "@/types/db/positions";
import { useSettingsSearch } from "@/contexts/SettingsSearchContext";

export default function PositionsPage() {
  const { colors } = useTheme();
  const { tenantId, userRoles } = useAuth();
  const permissions = getEntityPermissions(userRoles, "positions");
  const params = useLocalSearchParams<{ create?: string }>();
  const contextMenu = useContextMenu();
  const rateTypeButtonRef = useRef<View>(null);
  const payrollRateTypeButtonRef = useRef<View>(null);

  const [positions, setPositions] = useState<PositionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { searchValue } = useSettingsSearch("positions");

  // Filter positions by name and description
  const filteredPositions = useMemo(() => {
    if (!searchValue.trim()) return positions;
    const search = searchValue.toLowerCase();
    return positions.filter(
      (position) =>
        position.name?.toLowerCase().includes(search) || position.description?.toLowerCase().includes(search),
    );
  }, [positions, searchValue]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    supervisory_position: boolean;
    billing_rate: string;
    billing_rate_type: "hourly";
    payroll_rate_type: "base_add" | "hourly";
    payroll_rate_amount: string;
    payroll_rate_offset: string;
  }>({
    name: "",
    description: "",
    supervisory_position: false,
    billing_rate: "",
    billing_rate_type: "hourly",
    payroll_rate_type: "base_add",
    payroll_rate_amount: "",
    payroll_rate_offset: "",
  });
  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    billing_rate?: string;
    payroll_rate?: string;
  }>({});

  // Compute form validity for button enabling
  const isFormValid = useMemo(() => {
    if (!formData.name.trim()) return false;

    const billingRate = parseFloat(formData.billing_rate);
    if (!formData.billing_rate.trim() || isNaN(billingRate) || billingRate <= 0) return false;

    if (formData.payroll_rate_type === "base_add" && formData.payroll_rate_offset.trim()) {
      const offset = parseFloat(formData.payroll_rate_offset);
      if (isNaN(offset) || offset < 0) return false;
    }

    if (formData.payroll_rate_type === "hourly" && formData.payroll_rate_amount.trim()) {
      const amount = parseFloat(formData.payroll_rate_amount);
      if (isNaN(amount) || amount <= 0) return false;
    }

    return true;
  }, [formData]);

  // Validate and update errors in real-time
  const validateField = (field: "name" | "billing_rate" | "payroll_rate", value: string, type?: string) => {
    const newErrors = { ...validationErrors };

    if (field === "name") {
      if (!value.trim()) {
        newErrors.name = "Name is required";
      } else {
        delete newErrors.name;
      }
    }

    if (field === "billing_rate") {
      if (!value.trim()) {
        newErrors.billing_rate = "Billing rate is required";
      } else {
        const rate = parseFloat(value);
        if (isNaN(rate) || rate <= 0) {
          newErrors.billing_rate = "Invalid decimal value";
        } else {
          delete newErrors.billing_rate;
        }
      }
    }

    if (field === "payroll_rate") {
      if (!value.trim()) {
        delete newErrors.payroll_rate;
      } else if (type === "base_add") {
        const offset = parseFloat(value);
        if (isNaN(offset)) {
          newErrors.payroll_rate = "Invalid decimal value";
        } else if (offset < 0) {
          newErrors.payroll_rate = "Offset must be 0 or greater";
        } else {
          delete newErrors.payroll_rate;
        }
      } else {
        const amount = parseFloat(value);
        if (isNaN(amount) || amount <= 0) {
          newErrors.payroll_rate = "Invalid decimal value";
        } else {
          delete newErrors.payroll_rate;
        }
      }
    }

    setValidationErrors(newErrors);
  };

  // Load positions
  const loadPositions = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .schema("lists")
        .from("positions")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setPositions(data || []);
    } catch (err: any) {
      setError(err.message || "Failed to load positions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPositions();
  }, []);

  // Watch for create param changes
  useEffect(() => {
    if (params.create === "true") {
      setShowForm(true);
      setEditingId(null);
      setFormData({
        name: "",
        description: "",
        supervisory_position: false,
        billing_rate: "",
        billing_rate_type: "hourly",
        payroll_rate_type: "base_add",
        payroll_rate_amount: "",
        payroll_rate_offset: "",
      });
    }
  }, [params.create]);

  // Map legacy base_rate to base_add when loading into form
  const resolvePayrollType = (rate: string | undefined): "base_add" | "hourly" => {
    if (rate === "hourly") return rate;
    return "base_add"; // base_rate, daily, and anything else falls back to base_add
  };

  // Menu actions
  const handleEdit = (position: PositionType) => {
    setEditingId(position.id ?? null);

    const payrollRate = position.meta_data?.payroll_rate;
    const isBaseAdd = payrollRate?.rate === "base_add";

    setFormData({
      name: position.name,
      description: position.description || "",
      supervisory_position: position.meta_data?.supervisory_position ?? false,
      billing_rate: position.meta_data?.billing_rate?.amount?.toString() || position.billing_rate.toString(),
      billing_rate_type: "hourly",
      payroll_rate_type: resolvePayrollType(payrollRate?.rate),
      payroll_rate_amount:
        !isBaseAdd && payrollRate && payrollRate.rate !== "base_rate" && payrollRate.rate !== "daily"
          ? (payrollRate as any).amount?.toString() || ""
          : "",
      payroll_rate_offset: isBaseAdd && payrollRate ? (payrollRate as any).offset?.toString() || "" : "",
    });
    setShowForm(true);
    setError(null);
  };

  const handleDuplicate = (position: PositionType) => {
    setEditingId(null); // No ID means it's a new item

    const payrollRate = position.meta_data?.payroll_rate;
    const isBaseAdd = payrollRate?.rate === "base_add";

    setFormData({
      name: position.name,
      description: position.description || "",
      supervisory_position: position.meta_data?.supervisory_position ?? false,
      billing_rate: position.meta_data?.billing_rate?.amount?.toString() || position.billing_rate.toString(),
      billing_rate_type: "hourly",
      payroll_rate_type: resolvePayrollType(payrollRate?.rate),
      payroll_rate_amount:
        !isBaseAdd && payrollRate && payrollRate.rate !== "base_rate" && payrollRate.rate !== "daily"
          ? (payrollRate as any).amount?.toString() || ""
          : "",
      payroll_rate_offset: isBaseAdd && payrollRate ? (payrollRate as any).offset?.toString() || "" : "",
    });
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this position?")) return;

    try {
      const { error } = await supabase.schema("lists").from("positions").delete().eq("id", id);

      if (error) throw error;
      setPositions(positions.filter((p) => p.id !== id));
    } catch (err: any) {
      setError(err.message || "Failed to delete position");
    }
  };

  // Form submit handler (handles both add and update)
  const handleSubmit = async () => {
    if (!tenantId || !formData.name.trim() || !formData.billing_rate.trim()) {
      setError("Name and billing rate are required");
      return;
    }

    const billingRate = parseFloat(formData.billing_rate);
    if (isNaN(billingRate) || billingRate < 0) {
      setError("Billing rate must be a valid positive number");
      return;
    }

    try {
      // Build payroll_rate based on type
      let payroll_rate:
        | {
            rate: "base_add";
            offset: number;
          }
        | {
            rate: "hourly";
            amount: number;
          }
        | undefined;

      if (formData.payroll_rate_type === "base_add") {
        const offset = parseFloat(formData.payroll_rate_offset);
        if (!isNaN(offset) && offset >= 0) {
          payroll_rate = {
            rate: "base_add",
            offset,
          };
        }
      } else if (formData.payroll_rate_type === "hourly") {
        const amount = parseFloat(formData.payroll_rate_amount);
        if (!isNaN(amount) && amount > 0) {
          payroll_rate = {
            rate: formData.payroll_rate_type,
            amount,
          };
        }
      }

      const meta_data = {
        supervisory_position: formData.supervisory_position,
        billing_rate: {
          rate: formData.billing_rate_type,
          amount: billingRate,
        },
        ...(payroll_rate && { payroll_rate }),
      };

      if (editingId) {
        // Update existing position
        const { data, error } = await supabase
          .schema("lists")
          .from("positions")
          .update({
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            billing_rate: billingRate,
            meta_data,
          })
          .eq("id", editingId)
          .select()
          .single();

        if (error) throw error;
        setPositions(positions.map((p) => (p.id === editingId ? data : p)));
      } else {
        // Add new position
        const { data, error } = await supabase
          .schema("lists")
          .from("positions")
          .insert([
            {
              name: formData.name.trim(),
              description: formData.description.trim() || null,
              billing_rate: billingRate,
              tenant_id: tenantId,
              meta_data,
            },
          ])
          .select()
          .single();

        if (error) throw error;
        setPositions([...positions, data]);
      }

      // Reset form
      setFormData({
        name: "",
        description: "",
        supervisory_position: false,
        billing_rate: "",
        billing_rate_type: "hourly",
        payroll_rate_type: "base_add",
        payroll_rate_amount: "",
        payroll_rate_offset: "",
      });
      setValidationErrors({});
      setShowForm(false);
      setEditingId(null);
      setError(null);
      router.setParams({ create: undefined });
    } catch (err: any) {
      setError(err.message || (editingId ? "Failed to update position" : "Failed to add position"));
    }
  };

  const handleCancel = () => {
    setFormData({
      name: "",
      description: "",
      supervisory_position: false,
      billing_rate: "",
      billing_rate_type: "hourly",
      payroll_rate_type: "base_add",
      payroll_rate_amount: "",
      payroll_rate_offset: "",
    });
    setValidationErrors({});
    setShowForm(false);
    setEditingId(null);
    setError(null);
    router.setParams({ create: undefined });
  };

  const positionColor = colors.teal.border;
  const positionBackground = colors.teal.background;

  if (!permissions.read) {
    return <NoPermissionScreen entityName="positions" />;
  }

  return (
    <ScrollView style={styles.container}>
      <View>
        {loading ? (
          <ThemedText style={styles.centered}>Loading positions...</ThemedText>
        ) : error ? (
          <View style={styles.errorContainer}>
            <ThemedText type="defaultSemiBold" style={{ color: colors.red.text }}>
              {error}
            </ThemedText>
            <Button text="Retry" icon="refresh" style={{ marginTop: 8 }} onPress={loadPositions} />
          </View>
        ) : (
          <View style={styles.cardsContainer}>
            {permissions.create && showForm && !editingId && (
              <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={styles.editForm}>
                  <View style={styles.row}>
                    <TextInput
                      style={[
                        styles.input,
                        styles.flexInput,
                        { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                      ]}
                      placeholder="Name *"
                      placeholderTextColor={colors.placeholder}
                      value={formData.name}
                      onChangeText={(text) => {
                        setFormData({ ...formData, name: text });
                        validateField("name", text);
                      }}
                      autoFocus
                    />
                  </View>
                  <TextInput
                    style={[
                      styles.input,
                      styles.descriptionInput,
                      { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                    ]}
                    placeholder="Description (optional)"
                    placeholderTextColor={colors.placeholder}
                    value={formData.description}
                    onChangeText={(text) => setFormData({ ...formData, description: text })}
                    multiline
                  />
                  <View style={styles.toggleRow}>
                    <View>
                      <ThemedText type="defaultSemiBold">Supervisory Position</ThemedText>
                      <ThemedText style={{ fontSize: 12, opacity: 0.6 }}>
                        This position has supervisory responsibilities
                      </ThemedText>
                    </View>
                    <ToggleSwitch
                      value={formData.supervisory_position}
                      onValueChange={(value) => setFormData({ ...formData, supervisory_position: value })}
                    />
                  </View>
                  <View style={styles.row}>
                    <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Billing Rate:</ThemedText>
                    <Button
                      ref={rateTypeButtonRef}
                      text="Hourly"
                      icon="chevron-down"
                      iconPosition="right"
                      style={[styles.input, styles.rateTypeButton, { borderColor: colors.border }]}
                      textStyle={{ fontSize: 14 }}
                      onPress={() => {
                        contextMenu.setIgnoreRef(rateTypeButtonRef);
                        rateTypeButtonRef.current?.measureInWindow((pageX, pageY) =>
                          contextMenu.setLocation({ x: pageX, y: pageY + 45 }),
                        );
                        contextMenu.setMenuContent(
                          <View style={{ backgroundColor: colors.background, borderColor: colors.border }}>
                            <Button
                              text="Hourly"
                              style={styles.enumMenuItem}
                              onPress={() => {
                                setFormData({ ...formData, billing_rate_type: "hourly" });
                                contextMenu.setVisible(false);
                              }}
                            />
                          </View>,
                        );
                        contextMenu.setVisible(true);
                        contextMenu.setStyle({ backgroundColor: colors.background, overflow: "hidden" });
                      }}
                    />
                    <TextInput
                      style={[
                        styles.input,
                        styles.rateInput,
                        { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                      ]}
                      placeholder="Amount"
                      placeholderTextColor={colors.placeholder}
                      value={formData.billing_rate}
                      onChangeText={(text) => {
                        setFormData({ ...formData, billing_rate: text });
                        validateField("billing_rate", text);
                      }}
                      keyboardType="decimal-pad"
                    />
                  </View>
                  {validationErrors.billing_rate && (
                    <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>
                      {validationErrors.billing_rate}
                    </ThemedText>
                  )}
                  <View style={styles.row}>
                    <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Payroll Rate:</ThemedText>
                    <Button
                      ref={payrollRateTypeButtonRef}
                      text={formData.payroll_rate_type === "base_add" ? "Base +" : "Hourly"}
                      icon="chevron-down"
                      iconPosition="right"
                      style={[styles.input, styles.rateTypeButton, { borderColor: colors.border }]}
                      textStyle={{ fontSize: 14 }}
                      onPress={() => {
                        contextMenu.setIgnoreRef(payrollRateTypeButtonRef);
                        payrollRateTypeButtonRef.current?.measureInWindow((pageX, pageY) =>
                          contextMenu.setLocation({ x: pageX, y: pageY + 45 }),
                        );
                        contextMenu.setMenuContent(
                          <View style={{ backgroundColor: colors.background, borderColor: colors.border }}>
                            <Button
                              text="Base +"
                              style={styles.enumMenuItem}
                              onPress={() => {
                                setFormData({
                                  ...formData,
                                  payroll_rate_type: "base_add",
                                  payroll_rate_amount: "",
                                });
                                setValidationErrors({ ...validationErrors, payroll_rate: undefined });
                                contextMenu.setVisible(false);
                              }}
                            />
                            <Button
                              text="Hourly"
                              style={styles.enumMenuItem}
                              onPress={() => {
                                setFormData({
                                  ...formData,
                                  payroll_rate_type: "hourly",
                                  payroll_rate_offset: "",
                                });
                                setValidationErrors({ ...validationErrors, payroll_rate: undefined });
                                contextMenu.setVisible(false);
                              }}
                            />
                          </View>,
                        );
                        contextMenu.setVisible(true);
                        contextMenu.setStyle({ backgroundColor: colors.background, overflow: "hidden" });
                      }}
                    />
                    {formData.payroll_rate_type === "base_add" ? (
                      <TextInput
                        style={[
                          styles.input,
                          styles.rateInput,
                          {
                            borderColor: colors.border,
                            color: colors.text,
                            backgroundColor: colors.backgroundSecondary,
                          },
                        ]}
                        placeholder="Offset"
                        placeholderTextColor={colors.placeholder}
                        value={formData.payroll_rate_offset}
                        onChangeText={(text) => {
                          setFormData({ ...formData, payroll_rate_offset: text });
                          validateField("payroll_rate", text, "base_add");
                        }}
                        keyboardType="decimal-pad"
                      />
                    ) : (
                      <TextInput
                        style={[
                          styles.input,
                          styles.rateInput,
                          {
                            borderColor: colors.border,
                            color: colors.text,
                            backgroundColor: colors.backgroundSecondary,
                          },
                        ]}
                        placeholder="Amount"
                        placeholderTextColor={colors.placeholder}
                        value={formData.payroll_rate_amount}
                        onChangeText={(text) => {
                          setFormData({ ...formData, payroll_rate_amount: text });
                          validateField("payroll_rate", text, formData.payroll_rate_type);
                        }}
                        keyboardType="decimal-pad"
                      />
                    )}
                  </View>
                  <View style={{ justifyContent: "space-between", flexDirection: "row" }}>
                    {validationErrors.payroll_rate && (
                      <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>
                        {validationErrors.payroll_rate}
                      </ThemedText>
                    )}
                    <View style={styles.editActions}>
                      <Button
                        text="Cancel"
                        style={[styles.smallBtn, { borderColor: colors.border }]}
                        textStyle={{ fontSize: 13 }}
                        onPress={handleCancel}
                      />
                      <Button
                        text="Add"
                        icon="plus"
                        iconPosition="right"
                        style={[styles.smallBtn, { backgroundColor: colors.primary }]}
                        textStyle={{ fontSize: 13, color: "white" }}
                        onPress={handleSubmit}
                        enabled={isFormValid}
                      />
                    </View>
                  </View>
                </View>
              </View>
            )}

            {filteredPositions.map((position) => {
              const isEditing = editingId === position.id;

              return (
                <View
                  key={position.id}
                  style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}
                >
                  {isEditing ? (
                    <View style={styles.editForm}>
                      <View style={styles.row}>
                        <TextInput
                          style={[
                            styles.input,
                            styles.flexInput,
                            {
                              borderColor: colors.border,
                              color: colors.text,
                              backgroundColor: colors.backgroundSecondary,
                            },
                          ]}
                          placeholder="Name *"
                          placeholderTextColor={colors.placeholder}
                          value={formData.name}
                          onChangeText={(text) => {
                            setFormData({ ...formData, name: text });
                            validateField("name", text);
                          }}
                          autoFocus
                        />
                      </View>
                      {validationErrors.name && (
                        <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>
                          {validationErrors.name}
                        </ThemedText>
                      )}
                      <TextInput
                        style={[
                          styles.input,
                          styles.descriptionInput,
                          {
                            borderColor: colors.border,
                            color: colors.text,
                            backgroundColor: colors.backgroundSecondary,
                          },
                        ]}
                        placeholder="Description (optional)"
                        placeholderTextColor={colors.placeholder}
                        value={formData.description}
                        onChangeText={(text) => setFormData({ ...formData, description: text })}
                        multiline
                      />
                      <View style={styles.toggleRow}>
                        <View>
                          <ThemedText type="defaultSemiBold">Supervisory Position</ThemedText>
                          <ThemedText style={{ fontSize: 12, opacity: 0.6 }}>
                            This position has supervisory responsibilities
                          </ThemedText>
                        </View>
                        <ToggleSwitch
                          value={formData.supervisory_position}
                          onValueChange={(value) => setFormData({ ...formData, supervisory_position: value })}
                        />
                      </View>
                      <View style={styles.row}>
                        <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Billing Rate:</ThemedText>
                        <Button
                          ref={rateTypeButtonRef}
                          text="Hourly"
                          icon="chevron-down"
                          iconPosition="right"
                          style={[styles.input, styles.rateTypeButton, { borderColor: colors.border }]}
                          textStyle={{ fontSize: 14 }}
                          onPress={() => {
                            contextMenu.setIgnoreRef(rateTypeButtonRef);
                            rateTypeButtonRef.current?.measureInWindow((pageX, pageY) =>
                              contextMenu.setLocation({ x: pageX, y: pageY + 45 }),
                            );
                            contextMenu.setMenuContent(
                              <View style={{ backgroundColor: colors.background, borderColor: colors.border }}>
                                <Button
                                  text="Hourly"
                                  style={styles.enumMenuItem}
                                  onPress={() => {
                                    setFormData({ ...formData, billing_rate_type: "hourly" });
                                    contextMenu.setVisible(false);
                                  }}
                                />
                              </View>,
                            );
                            contextMenu.setVisible(true);
                            contextMenu.setStyle({ backgroundColor: colors.background, overflow: "hidden" });
                          }}
                        />
                        <TextInput
                          style={[
                            styles.input,
                            styles.rateInput,
                            {
                              borderColor: colors.border,
                              color: colors.text,
                              backgroundColor: colors.backgroundSecondary,
                            },
                          ]}
                          placeholder="Amount"
                          placeholderTextColor={colors.placeholder}
                          value={formData.billing_rate}
                          onChangeText={(text) => {
                            setFormData({ ...formData, billing_rate: text });
                            validateField("billing_rate", text);
                          }}
                          keyboardType="decimal-pad"
                        />
                      </View>
                      {validationErrors.billing_rate && (
                        <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>
                          {validationErrors.billing_rate}
                        </ThemedText>
                      )}
                      <View style={styles.row}>
                        <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Payroll Rate:</ThemedText>
                        <Button
                          ref={payrollRateTypeButtonRef}
                          text={formData.payroll_rate_type === "base_add" ? "Base +" : "Hourly"}
                          icon="chevron-down"
                          iconPosition="right"
                          style={[styles.input, styles.rateTypeButton, { borderColor: colors.border }]}
                          textStyle={{ fontSize: 14 }}
                          onPress={() => {
                            contextMenu.setIgnoreRef(payrollRateTypeButtonRef);
                            payrollRateTypeButtonRef.current?.measureInWindow((pageX, pageY) =>
                              contextMenu.setLocation({ x: pageX, y: pageY + 45 }),
                            );
                            contextMenu.setMenuContent(
                              <View style={{ backgroundColor: colors.background, borderColor: colors.border }}>
                                <Button
                                  text="Base +"
                                  style={styles.enumMenuItem}
                                  onPress={() => {
                                    setFormData({
                                      ...formData,
                                      payroll_rate_type: "base_add",
                                      payroll_rate_amount: "",
                                    });
                                    contextMenu.setVisible(false);
                                  }}
                                />
                                <Button
                                  text="Hourly"
                                  style={styles.enumMenuItem}
                                  onPress={() => {
                                    setFormData({
                                      ...formData,
                                      payroll_rate_type: "hourly",
                                      payroll_rate_offset: "",
                                    });
                                    contextMenu.setVisible(false);
                                  }}
                                />
                              </View>,
                            );
                            contextMenu.setVisible(true);
                            contextMenu.setStyle({ backgroundColor: colors.background, overflow: "hidden" });
                          }}
                        />
                        {formData.payroll_rate_type === "base_add" ? (
                          <TextInput
                            style={[
                              styles.input,
                              styles.rateInput,
                              {
                                borderColor: colors.border,
                                color: colors.text,
                                backgroundColor: colors.backgroundSecondary,
                              },
                            ]}
                            placeholder="Offset"
                            placeholderTextColor={colors.placeholder}
                            value={formData.payroll_rate_offset}
                            onChangeText={(text) => {
                              setFormData({ ...formData, payroll_rate_offset: text });
                              validateField("payroll_rate", text, "base_add");
                            }}
                            keyboardType="decimal-pad"
                          />
                        ) : (
                          <TextInput
                            style={[
                              styles.input,
                              styles.rateInput,
                              {
                                borderColor: colors.border,
                                color: colors.text,
                                backgroundColor: colors.backgroundSecondary,
                              },
                            ]}
                            placeholder="Amount"
                            placeholderTextColor={colors.placeholder}
                            value={formData.payroll_rate_amount}
                            onChangeText={(text) => {
                              setFormData({ ...formData, payroll_rate_amount: text });
                              validateField("payroll_rate", text, formData.payroll_rate_type);
                            }}
                            keyboardType="decimal-pad"
                          />
                        )}
                      </View>
                      <View style={{ justifyContent: "space-between", flexDirection: "row" }}>
                        {validationErrors.payroll_rate && (
                          <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>
                            {validationErrors.payroll_rate}
                          </ThemedText>
                        )}
                        <View style={styles.editActions}>
                          <Button
                            text="Cancel"
                            style={[styles.smallBtn, { borderColor: colors.border }]}
                            textStyle={{ fontSize: 13 }}
                            onPress={handleCancel}
                          />
                          <Button
                            text="Update"
                            icon="check"
                            iconPosition="right"
                            style={[styles.smallBtn, { backgroundColor: colors.primary }]}
                            textStyle={{ fontSize: 13, color: "white" }}
                            onPress={handleSubmit}
                            enabled={isFormValid}
                          />
                        </View>
                      </View>
                    </View>
                  ) : (
                    <View style={styles.itemContent}>
                      <View style={styles.itemInfo}>
                        <View style={styles.itemHeader}>
                          <ThemedText type="defaultSemiBold">{position.name}</ThemedText>
                          <Badge
                            label={"Billing Rate"}
                            tone="teal"
                            text={`$${(position.meta_data?.billing_rate?.amount || position.billing_rate).toFixed(2)}/hr`}
                          />
                          <Badge
                            label={"Payroll Rate"}
                            tone="amber"
                            text={
                              position.meta_data?.payroll_rate?.rate === "base_rate"
                                ? `${(position.meta_data.payroll_rate.multiplier * 100).toFixed(0)}% (legacy)`
                                : position.meta_data?.payroll_rate?.rate === "base_add"
                                  ? `${position.meta_data.payroll_rate.offset >= 0 ? "+" : ""}$${position.meta_data.payroll_rate.offset.toFixed(2)}`
                                  : `$${position.meta_data?.payroll_rate?.amount?.toFixed(2) ?? "0.00"}/hr`
                            }
                          />
                          {position.meta_data?.supervisory_position && (
                            <Badge label="Supervisory" tone="blue" text="Yes" />
                          )}
                          <Menu
                            buttonStyle={{
                              marginLeft: "auto",
                              alignSelf: "flex-start",
                              borderWidth: 0,
                              backgroundColor: "transparent",
                            }}
                            hideArchive={true}
                            canEdit={permissions.update}
                            canDuplicate={permissions.create}
                            canDelete={permissions.delete}
                            onEdit={() => handleEdit(position)}
                            onDuplicate={() => handleDuplicate(position)}
                            onDelete={() => position.id && handleDelete(position.id)}
                          />
                        </View>
                        {position.description && (
                          <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
                            {position.description}
                          </ThemedText>
                        )}
                      </View>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 16,
    flex: 1,
  },
  section: {
    padding: 16,
  },
  cardsContainer: {
    gap: 12,
  },
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    overflow: "hidden",
  },
  itemContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
  },
  itemInfo: {
    flex: 1,
    gap: 4,
  },
  itemHeader: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 8,
  },
  badgeRow: {
    flexDirection: "row",
    gap: 6,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  rateText: {
    fontSize: 11,
    fontWeight: "700",
  },
  description: {
    fontSize: 13,
  },
  errorText: {
    fontSize: 12,
    marginTop: 2,
  },
  editForm: {
    padding: 16,
    gap: 12,
  },
  row: {
    flexDirection: "row",
    gap: 8,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 8,
  },
  input: {
    height: 40,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  flexInput: {
    flex: 1,
  },
  rateInput: {
    width: 100,
  },
  label: {
    fontSize: 14,
    alignSelf: "center",
    minWidth: 100,
  },
  rateTypeButton: {
    width: 120,
    backgroundColor: "transparent",
    justifyContent: "space-between",
    paddingHorizontal: 8,
  },
  descriptionInput: {
    height: 60,
    paddingTop: 10,
    textAlignVertical: "top",
  },
  enumMenuItem: {
    backgroundColor: "transparent",
    borderWidth: 0,
    justifyContent: "flex-start",
    gap: 8,
  },
  editActions: {
    marginLeft: "auto",
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
  },
  smallBtn: {
    height: 32,
    gap: 8,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  addBtn: {
    backgroundColor: "transparent",
    gap: 8,
    borderWidth: 1,
  },
  centered: {
    textAlign: "center",
    paddingVertical: 32,
    opacity: 0.7,
  },
  errorContainer: {
    alignItems: "center",
    paddingVertical: 32,
  },
});
